import React, {
  useState, useCallback, useEffect, useLayoutEffect, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTheme } from 'emotion-theming';
import Shimmer from '../../../../shared/_TBComponents/Shimmer';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import useIsTouchDevice from '../../../../shared/hooks/useIsTouchDevice';
import SwapPropDropdown from './SwapPropDropdown';
import AddPropertyColumnComponent from './AddPropertyColumnComponent';
import StatusBadgeScroller from './StatusBadgeScroller';
import Text from '../../../../shared/components/Text/Text';
import {
  saveCalendarExercise,
  updateReduxCalendarItem,
  updateReduxProgramItem,
  focusNextPreSaveItem,
  focusNextPreSaveProgramItem,
  setPreSaveTableLocalState,
  saveNonDateBasedWorkoutExercise,
} from '../../ducks/calendarActions';
import capitalizeWords from '../../../../shared/utils/capitalizeWords';
import generateUniqueId from '../../../../shared/utils/generateUniqueId';
import hexToRgba from '../../../../shared/utils/hexToRgba';
import { SUPERSET_DEFAULTS } from '../../../../shared/constants';
import { showErrorToast } from '../../../../shared/utils/toastManager';
import { normalizeRepArray } from '../../../../shared/utils/normalizeRepToken';
import wipeRepKeywordOnMutation from '../../../../shared/utils/wipeRepKeywordOnMutation';
import {
  mapLegacyTextColor, StateBadge, SetsStepper, SetsCountLabel, SetsSubtext, SetsFieldGroup,
} from './TransposedTableShared';
import { claimArrowKeys } from '../../utils/arrowKeyOwnership';
import { useEnterSavesForm } from './hooks/useEnterSavesForm';
import { FOCUS_FILL_VAR } from '../../../../shared/theme/focusStyles';

// Accepts: 55, 55.5, 55-60, 55.5-60.8, 5+, 4.5+
const VALUE_PATTERN = /^\d+(\.\d+)?(-\d+(\.\d+)?|\+)?$/;
const MAX_FIELD_VALUE = 10000;
const isValidRangeToken = (tok) => {
  if (!VALUE_PATTERN.test(tok)) return false;
  const parts = tok.replace('+', '').split('-');
  if (!parts.every((p) => parseFloat(p) <= MAX_FIELD_VALUE)) return false;
  if (parts.length === 2 && parseFloat(parts[0]) > parseFloat(parts[1])) return false;
  return true;
};
// For submit: pure numbers go as floats; range/+ tokens go as strings (backend accepts both)
const toFieldValue = (token) => {
  if (/^\d+(\.\d+)?$/.test(token)) return parseFloat(token);
  return token;
};

const FormWrapper = styled('form')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${(props) => props.background || props.theme.colors.surfaceInput};
  border: ${(props) => (!props.isError ? `1px solid ${props.theme.colors.borderLight}` : '1px solid red')};
  text-align: center;
  border-radius: 4px;
  color: ${mapLegacyTextColor};

  ::placeholder {
    color: ${mapLegacyTextColor};
  }
`;

const RowValueContainer = styled('div')`
  flex: 1;
  min-width: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

/** Transposed-layout styled components — properties as rows, single
 * input per row (pre-save data model: one value per property applies
 * to every set on save). The Sets count drives how many sets get
 * created when the form is submitted. */
const SetsCountHeader = styled('div')`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  /** Extra bottom padding reserves room for the out-of-flow
   * "# of Sets: 1-10" subtext under the stepper. */
  padding: 20px 0 22px;
`;

/** Superset picker — keyboard-friendly alternative to the kebab quick
 * action. Sits next to the Sets stepper in the header. */
const SupersetWrapper = styled('div')`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SupersetButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 8px 0 6px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  &:focus { outline: none; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 2px #BFDBFE; }
`;

const SupersetSwatch = styled('div')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: ${(props) => props.background || props.theme.colors.surfaceSecondary};
  color: ${({ theme }) => theme.colors.text};
  font-size: 13px;
  font-weight: 600;
`;

/** Empty-state placeholder for the superset trigger. Plain muted text
 * instead of a tinted swatch — reads as "nothing here yet" rather than
 * looking like a filled chip. */
const SupersetEmptyHint = styled('span')`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 2px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/** Portaled to <body> so it stacks above the table/list virtualization
 * (which carries its own z-index up to ~2000 inside the calendar
 * column) and any sibling row elements. Anchored under the trigger
 * via `position: fixed` + measured coords. */
const SupersetPopover = styled('div')`
  position: fixed;
  z-index: 1100;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px;
  width: 220px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
`;

const SupersetChip = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 4px;
  background: ${(props) => props.background || 'white'};
  border: 2px solid ${(props) => (props.isSelected ? props.theme.colors.link : props.theme.colors.borderLight)};
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => (props.muted ? props.theme.colors.textSecondary : props.theme.colors.text)};
  cursor: pointer;
  &:hover { border-color: ${(props) => (props.isSelected ? props.theme.colors.link : props.theme.colors.textSecondary)}; }
  &:focus { outline: none; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 2px #BFDBFE; }
`;

const StepperButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  font-family: 'Nunito Sans';
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHoverStrong};
  }
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    background: ${FOCUS_FILL_VAR};
    box-shadow: inset 0 0 0 2px #BFDBFE;
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.textDisabled};
    cursor: not-allowed;
  }
`;

const StepperInput = styled('input')`
  width: 35px;
  border: none;
  border-left: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: transparent;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &:focus { outline: none; }
`;

const TransposedTable = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TransposedRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  width: 100%;
`;

const PropertyLabelCell = styled('div')`
  display: flex;
  align-items: center;
  min-width: 120px;
  max-width: 120px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text};
`;

/** Single input per property. Grows to fill remaining width with a
 * lower bound; the legacy `RowValueContainer` and the `<input>` itself
 * carry intrinsic min-widths that we override here so the field
 * actually flexes. */
const PropertyInputCell = styled('div')`
  display: flex;
  align-items: center;
  flex: 1 1 80px;
  min-width: 80px;
  > div {
    min-width: 0;
    width: 100%;
  }
  input {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
`;

const TransposedFooter = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 8px;
  /** Bottom room so the Add button's focus ring isn't clipped by an
   * ancestor's overflow at the edge of the card. */
  padding-bottom: 4px;
`;

/** Constrains the AddPropertyColumnComponent to a button-sized slot
 * (instead of stretching across the footer row). */
const AddPropertySlot = styled('div')`
  display: flex;
  width: auto;
`;

/** Pre-save Add Property trigger — same shape as the saved-edit
 * version but with a blue dashed border + blue icon square to signal
 * this is the build-mode flow. */
const AddPropertyTrigger = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px dashed ${({ theme }) => theme.colors.link};
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.link};
  background: transparent;
  cursor: pointer;
  user-select: none;
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const AddPropertyTriggerIcon = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.link};
`;

/** Compact variant of the legacy save button — sized to sit inline
 * with the Add Property trigger on the same footer row. */
const CompactSaveButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #10cd8c;
  color: ${(props) => (props.shimmer ? 'transparent' : 'white')};
  /** Transparent 1px border so the box-sizing matches AddPropertyTrigger
   * (which carries a real 1px dashed border) — keeps the heights aligned
   * pixel-for-pixel. */
  border: 1px solid transparent;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  cursor: pointer;
  &:hover { opacity: 0.92; }
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    /** White gap + light-blue ring so the focus reads clearly against
     * the green fill (a single ring directly on green was hard to see). */
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.surface}, 0 0 0 4px #BFDBFE;
  }
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  position: relative;
`;

const InputWithErrorWrapper = styled('div')`
  position: relative;
  width: 100%;
`;

const InputError = styled('div')`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: -2px;
  font-size: 11px;
  font-family: 'Nunito Sans';
  color: red;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: default;
`;

const ShimmerRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  border-radius: 6px;
  color: transparent;
  margin-left: 5px;
  margin-right: 5px;
`;

/** Arrow-key focus steps inside the superset popover's 6-column grid. */
const SUPERSET_ARROW_STEPS = {
  ArrowRight: 1,
  ArrowLeft: -1,
  ArrowDown: 6,
  ArrowUp: -6,
};

const PreSaveTableTransposed = ({
  completionOnly,
  coachCompletion,
  isLeftAndRight,
  contentIndex,
  content,
  trackRepCount,
  trackVolumeLoad,
  localAdditionalInfo,
  swapDropdownSelectedExercise,
  forcePrUpdate,
  disablePrUpdate,
  measure,
  date,
  dayNumber,
  setCoachCompletion,
  setCompletionOnly,
  setIsLeftAndRight,
  setTrackVolumeLoad,
  setTrackRepCount,
  setForcePrUpdate,
  setDisablePrUpdate,
}) => {
  const { colors } = useTheme();
  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);

  /** Enter anywhere in this table saves it, exactly as clicking SAVE does —
   * blurring the focused cell first so its onBlur normalization runs. Stands
   * down inside month view's popover, which owns Enter there. */
  useEnterSavesForm(tableContainerRef);
  const [setNumberValue, setSetNumberValue] = useState(1);
  const [percentPR, setPercentPR] = useState('');
  const [isBodyweight, setIsBodyweight] = useState(!!content?.defaultOptions?.liftRepsOnly);
  const [isTime, setIsTime] = useState(false);
  const [errors, setErrors] = useState({
    reps: null,
    load: null,
    percentPR: null,
  });
  const setFieldError = useCallback((field, msg) => {
    setErrors((prev) => ({ ...prev, [field]: msg }));
  }, []);
  const errorTooltipId = `lifts-presave-error-tooltip-${content.assignedId}`;
  const [tempo, setTempo] = useState(null);
  const [rest, setRest] = useState(null);
  const [load, setLoad] = useState(null);
  const [loadInputValue, setLoadInputValue] = useState(''); // Track the raw load input string
  const [isAdding, setIsAdding] = useState(false);
  const [peakPowerEnum, setPeakPowerEnum] = useState(1);
  /** `content.groupingLetter`/`content.groupingColorCode` (Redux) are the
   * single source of truth for grouping — read directly rather than
   * mirrored into local state, so this picker and the older
   * dropdown/kebab/bulk-action superset pickers (SuperSetPickerPopup)
   * always agree, and submission always sends whichever value was picked
   * last regardless of which picker was used. */
  const [isSupersetOpen, setIsSupersetOpen] = useState(false);
  /** Fixed-position coords for the portaled popover, measured from the
   * trigger's bounding rect. `null` until the popover is opened. */
  const [supersetPopoverPos, setSupersetPopoverPos] = useState(null);
  const supersetButtonRef = useRef(null);
  const supersetPopoverRef = useRef(null);
  const supersetChipRefs = useRef([]);
  const [supersetFocusIndex, setSupersetFocusIndex] = useState(0);
  const [repsInputValue, setRepsInputValue] = useState(''); // Track the raw reps input string

  // Generate unique IDs for tooltips
  const repsTooltipId = `reps-tooltip-${content.assignedId}`;
  const timeTooltipId = `time-tooltip-${content.assignedId}`;
  const peakPowerTooltipId = `peak-power-tooltip-${content.assignedId}`;
  const velocityTooltipId = `velocity-tooltip-${content.assignedId}`;

  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const endpointFormattedDate = moment(date).format('YYYY-MM-DD');
  const focusTarget = useSelector((state) => state.calendar.data.focusTarget);
  const isTouchDevice = useIsTouchDevice();
  const preSaveTableLocalStateForItem = useSelector(
    (state) => state.calendar.data.preSaveTableLocalState[content.assignedId],
  );

  const [exerciseData, setExerciseData] = useState({
    reps: [],
    load: null,
    rest: null,
    tempo: null,
    velocity: null,
    peakPower: null,
  });

  const [primaryRows, setPrimaryRows] = useState([
    { name: 'set' },
    { name: isTime ? 'time' : 'reps' },
    { name: isBodyweight ? 'bodyweight' : 'load' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'tempo' },
    { name: 'velocity' },
    { name: 'peak power', label: 'Peak Power (w)' },
    { name: '%pr' },
    { name: 'add-property', label: 'Add Property' },
  ]);

  const [primaryRowSwitch, setPrimaryRowSwitch] = useState({
    set: true,
    reps: true,
    load: false,
    bodyweight: !!content.defaultOptions?.liftRepsOnly,
    rest: !!content?.defaultOptions?.liftRestTime,
    tempo: false,
    velocity: !!content?.defaultOptions?.barSpeed,
    'peak power': !!content?.defaultOptions?.peakPower,
    '%pr': false,
    'add-property': true,
  });

  const setPrimaryRowSwitchState = (newState) => {
    setPrimaryRowSwitch(newState);
  };

  const setTempoFunction = (value) => {
    setTempo(value);
  };

  const setRestFunction = (value) => {
    const intRest = parseInt(value, 10);
    // Prevent negative values
    if (intRest < 0) return;
    setRest(intRest);
  };

  const setPercentPRFunction = (value) => {
    setPercentPR(value);

    if (!value) {
      setFieldError('percentPR', null);
      return;
    }

    const prArray = value.split(',').map((pr) => pr.trim()).filter(Boolean);
    const setCount = parseInt(setNumberValue, 10);

    const invalidValues = [];
    prArray.forEach((pr, index) => {
      if (!/^\d+$/.test(pr)) {
        invalidValues.push({ value: pr, position: index + 1 });
      }
    });

    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setFieldError('percentPR', `Invalid %PR format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use whole numbers only.`);
      } else {
        const list = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setFieldError('percentPR', `Invalid %PR format for values: ${list}. Use whole numbers only.`);
      }
      return;
    }

    if (prArray.length !== setCount && prArray.length !== 1 && prArray.length > 0) {
      setFieldError('percentPR', 'Enter a %PR value to apply to all sets.');
    } else {
      setFieldError('percentPR', null);
    }
  };

  /** This useEffect detects if the user chooses a different exercise
   * from the item dropdownn select and updates the pre saved item in redux
   * so that the table structure and content updates to match the selected exercise
   */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const updatedItem = {
        ...swapDropdownSelectedExercise,
        title: swapDropdownSelectedExercise.name,
        exerciseId: parseInt(swapDropdownSelectedExercise.id, 10),
        id: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        assignedId: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        preSave: true,
        type: swapDropdownSelectedExercise?.type || 'T',
      };
      if (programType === 'free-form-program') {
        dispatch(
          updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, contentIndex),
        );
      } else {
        dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, contentIndex));
      }
    }
  }, [swapDropdownSelectedExercise]);

  const addPropertyInitialized = useRef(false);

  // Turn off 'add-property' when all optional properties are enabled
  useEffect(() => {
    // Skip on initial mount to allow backup restoration to complete
    if (!addPropertyInitialized.current) {
      addPropertyInitialized.current = true;
      return;
    }

    const allOptionalPropertiesEnabled = primaryRowSwitch.rest
      && primaryRowSwitch.tempo
      && primaryRowSwitch.velocity
      && primaryRowSwitch['peak power']
      && primaryRowSwitch['%pr']
      && primaryRowSwitch.load;

    if (allOptionalPropertiesEnabled && primaryRowSwitch['add-property']) {
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': false,
      }));
    } else if (!allOptionalPropertiesEnabled && !primaryRowSwitch['add-property']) {
      // Re-enable add-property if any optional property is turned off
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': true,
      }));
    }
  }, [
    primaryRowSwitch.rest,
    primaryRowSwitch.tempo,
    primaryRowSwitch.velocity,
    primaryRowSwitch['peak power'],
    primaryRowSwitch['%pr'],
    primaryRowSwitch.load,
  ]);

  const hasMounted = useRef(false);

  /** Re-measure the virtualized list cell whenever something changes
   * the *vertical* height of this pre-save card. Pre-save sets stay
   * conceptually in one row (single value per property), so vertical
   * height is driven by:
   *   - Property visibility (primaryRowSwitch: add/remove rows)
   *   - Error messages appearing/disappearing under the reps/load input
   *   - isBodyweight / isTime swapping the visible property
   *   - The kebab-toggle status pills wrapping in the header
   * Skip the first run because the CellMeasurer wrapper already
   * triggers its initial measure on mount. */
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    if (measure) {
      measure();
    }
  }, [
    primaryRowSwitch,
    errors,
    isBodyweight,
    isTime,
    isLeftAndRight,
    coachCompletion,
    completionOnly,
    forcePrUpdate,
    disablePrUpdate,
    trackVolumeLoad,
    trackRepCount,
  ]);

  useEffect(() => {
    if (isBodyweight) {
      setLoad(null);
      setLoadInputValue('');
    }
  }, [isBodyweight]);

  // When toggling reps↔time: swap AMAP↔ALAP tokens in the value (if present),
  // then re-run validation against the new keyword — without wiping user input.
  useEffect(() => {
    if (!repsInputValue) return;
    const fromKeyword = isTime ? 'AMAP' : 'ALAP';
    const toKeyword = isTime ? 'ALAP' : 'AMAP';
    const swapped = repsInputValue
      .split(',')
      .map((tok) => {
        const trimmed = tok.trim();
        const lead = tok.slice(0, tok.length - tok.trimStart().length);
        const trail = tok.slice(tok.trimEnd().length);
        if (trimmed.toUpperCase() === fromKeyword) return `${lead}${toKeyword}${trail}`;
        return tok;
      })
      .join(',');
    if (swapped !== repsInputValue) setRepsInputValue(swapped);
    // eslint-disable-next-line no-use-before-define
    handleInputChange(isTime ? 'time' : 'reps', swapped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTime]);

  // This useEffect is responsible for managing focus on input fields when the focusTarget changes.
  // It checks if the current focusTarget's assignedId matches the content's assignedId.
  // If they match, it finds the first input element
  // with a data-focus-target attribute equal to the content's assignedId.
  // Once found, it sets a timeout to focus on this
  // input element after a short delay, ensuring the DOM is ready.
  useEffect(() => {
    if (!isTouchDevice && focusTarget?.assignedId === content.assignedId) {
      const firstInput = document.querySelector(`[data-focus-target="${content.assignedId}"]`);
      if (firstInput) {
        setTimeout(() => {
          firstInput.focus();
        }, 100); // Small delay to ensure DOM is ready
      }
    }
  }, [isTouchDevice, focusTarget, content.assignedId]);

  // Initialize state from Redux backup if it exists
  const backupState = preSaveTableLocalStateForItem;
  const hasRestoredBackupRef = useRef(null);

  useEffect(() => {
    // Only restore once per assignedId — ref guard prevents re-execution
    // when auto-save writes back to the same backup slice
    if (hasRestoredBackupRef.current === content.assignedId) return;
    if (!backupState) return;

    hasRestoredBackupRef.current = content.assignedId;

    if (backupState.setNumberValue !== undefined) setSetNumberValue(backupState.setNumberValue);
    if (backupState.percentPR !== undefined) setPercentPR(backupState.percentPR);
    if (backupState.isBodyweight !== undefined) setIsBodyweight(backupState.isBodyweight);
    if (backupState.isTime !== undefined) setIsTime(backupState.isTime);
    const shouldNormalizeToAlap = !!backupState.isTime;
    const toAlap = (s) => (typeof s === 'string' && s.toUpperCase() === 'AMAP' ? 'ALAP' : s);
    if (backupState.tempo !== undefined) setTempo(backupState.tempo);
    if (backupState.rest !== undefined) setRest(backupState.rest);
    if (backupState.load !== undefined) setLoad(backupState.load);
    if (backupState.loadInputValue !== undefined) setLoadInputValue(backupState.loadInputValue);
    if (backupState.peakPowerEnum !== undefined) setPeakPowerEnum(backupState.peakPowerEnum);
    if (backupState.repsInputValue !== undefined) {
      setRepsInputValue(
        shouldNormalizeToAlap
          ? backupState.repsInputValue.split(',').map((v) => toAlap(v.trim())).join(', ')
          : backupState.repsInputValue,
      );
    }
    if (backupState.exerciseData !== undefined) {
      setExerciseData(
        shouldNormalizeToAlap && Array.isArray(backupState.exerciseData?.reps)
          ? { ...backupState.exerciseData, reps: backupState.exerciseData.reps.map(toAlap) }
          : backupState.exerciseData,
      );
    }
    if (backupState.primaryRowSwitch !== undefined) {
      setPrimaryRowSwitch(backupState.primaryRowSwitch);
    }
    if (backupState.primaryRows !== undefined) setPrimaryRows(backupState.primaryRows);
    if (backupState.coachCompletion !== undefined) setCoachCompletion(backupState.coachCompletion);
    if (backupState.completionOnly !== undefined) setCompletionOnly(backupState.completionOnly);
    if (backupState.isLeftAndRight !== undefined) setIsLeftAndRight(backupState.isLeftAndRight);
    if (backupState.trackVolumeLoad !== undefined) setTrackVolumeLoad(backupState.trackVolumeLoad);
    if (backupState.trackRepCount !== undefined) setTrackRepCount(backupState.trackRepCount);
    if (backupState.forcePrUpdate !== undefined) setForcePrUpdate(backupState.forcePrUpdate);
    if (backupState.disablePrUpdate !== undefined) setDisablePrUpdate(backupState.disablePrUpdate);
  }, [
    content.assignedId,
    backupState,
    setCoachCompletion,
    setCompletionOnly,
    setIsLeftAndRight,
    setTrackVolumeLoad,
    setTrackRepCount,
    setForcePrUpdate,
    setDisablePrUpdate,
  ]);

  // Save current state to Redux backup on blur
  const saveStateToRedux = useCallback(() => {
    const stateObject = {
      setNumberValue,
      percentPR,
      isBodyweight,
      isTime,
      tempo,
      rest,
      load,
      loadInputValue,
      peakPowerEnum,
      repsInputValue,
      exerciseData,
      primaryRowSwitch,
      primaryRows,
      coachCompletion,
      completionOnly,
      isLeftAndRight,
      trackVolumeLoad,
      trackRepCount,
      forcePrUpdate,
      disablePrUpdate,
    };
    dispatch(setPreSaveTableLocalState(content.assignedId, stateObject));
  }, [
    setNumberValue, percentPR, isBodyweight, isTime, tempo, rest, load,
    loadInputValue, peakPowerEnum, repsInputValue, exerciseData, primaryRowSwitch, primaryRows,
    coachCompletion, completionOnly, isLeftAndRight, trackVolumeLoad,
    trackRepCount, forcePrUpdate, disablePrUpdate,
    content.assignedId, dispatch,
  ]);

  const autoSaveInitialized = useRef(false);

  // Auto-save state to Redux on every change (not just blur)
  // This ensures state is preserved when component re-renders (e.g., superset selection)
  useEffect(() => {
    // Skip the initial render cycle - only save after user makes changes
    if (!autoSaveInitialized.current) {
      autoSaveInitialized.current = true;
      return;
    }
    saveStateToRedux();
  }, [saveStateToRedux]);

  const handleInputChange = (field, value) => {
    if (field === 'reps' || field === 'time') {
      setRepsInputValue(value);

      const keyword = field === 'time' ? 'ALAP' : 'AMAP';
      const fieldLabel = field === 'time' ? 'time' : 'reps';

      // Check for partial "custom" input
      const trimmedValue = value.trim().toLowerCase();
      if (['c', 'cu', 'cus', 'cust'].includes(trimmedValue)) {
        setFieldError('reps', 'If you would like to program multiple sets, please enter them as comma separated values, e.g. 12,10,8 to match the number of sets');
        return;
      }

      const repsArray = value
        .split(',')
        .map((rep) => rep.trim())
        .filter(Boolean);

      const setCount = parseInt(setNumberValue, 10);
      const {
        isValid: allValid,
        values: normalizedReps,
        invalidValues,
      } = normalizeRepArray(repsArray, [keyword]);

      setExerciseData((prev) => ({
        ...prev,
        reps: normalizedReps,
      }));

      if (!allValid) {
        if (invalidValues.length === 1) {
          setFieldError('reps', `Invalid format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "${keyword}".`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
          setFieldError('reps', `Invalid format for values: ${invalidList}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "${keyword}".`);
        }
        return;
      }

      if (repsArray.length !== setCount && repsArray.length !== 1 && repsArray.length > 0) {
        setFieldError('reps', `Enter a number for ${fieldLabel} to apply to all sets.`);
      } else {
        setFieldError('reps', null);
      }
    } else if (field === 'sets') {
      // Handle sets field change and re-validate reps
      setSetNumberValue(value);

      // Re-validate existing reps against new set count
      if (exerciseData.reps && exerciseData.reps.length > 0) {
        const newSetCount = parseInt(value, 10);
        const repsArray = exerciseData.reps;

        // Check if reps count matches new set count
        if (repsArray.length !== newSetCount && repsArray.length !== 1 && repsArray.length > 0) {
          setFieldError('reps', 'Enter a number for reps to apply to all sets.');
        } else {
          setFieldError('reps', null);
        }
      }

      // Re-validate existing load values against new set count
      if (loadInputValue && loadInputValue.trim() !== '') {
        const newSetCount = parseInt(value, 10);
        const loadArray = loadInputValue
          .split(',')
          .map((loadVal) => loadVal.trim())
          .filter(Boolean);

        // Check if load count matches new set count
        if (loadArray.length !== newSetCount && loadArray.length !== 1 && loadArray.length > 0) {
          setFieldError('load', 'Enter a number for load to apply to all sets.');
        } else {
          setFieldError('load', null);
        }
      }
    } else {
      // Update other fields normally
      setExerciseData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleRepsBlur = () => {
    const keyword = isTime ? 'ALAP' : 'AMAP';
    if (Array.isArray(exerciseData.reps)) {
      const setCount = parseInt(setNumberValue, 10);

      const {
        isValid: allValid,
        values: normalizedTokens,
        invalidValues,
      } = normalizeRepArray(exerciseData.reps, [keyword]);

      if (!allValid) {
        if (invalidValues.length === 1) {
          setFieldError('reps', `Invalid format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "${keyword}".`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('reps', `Invalid format for values: ${invalidList}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "${keyword}".`);
        }
        return;
      }

      // Normalize single values to fill all sets on blur
      if (normalizedTokens.length === 1) {
        const filledReps = Array(setCount).fill(normalizedTokens[0]);
        setExerciseData((prev) => ({
          ...prev,
          reps: filledReps,
        }));
        // Update the input display to show the normalized version
        setRepsInputValue(filledReps.join(', '));
        setFieldError('reps', null);
      } else if (normalizedTokens.length === setCount) {
        setExerciseData((prev) => ({
          ...prev,
          reps: normalizedTokens,
        }));
        setRepsInputValue(normalizedTokens.join(', '));
        setFieldError('reps', null);
      } else {
        setFieldError('reps', `Expected ${setCount} values or 1 value (to apply to all sets), but have ${normalizedTokens.length} values.`);
      }
    } else {
      setFieldError('reps', `Invalid input type. Expected comma-separated values but received: ${typeof exerciseData.reps}.`);
    }
  };

  const handleRepsKeyDown = (e) => {
    const { key, target } = e;
    const { value, selectionStart, selectionEnd } = target;
    const keyword = isTime ? 'ALAP' : 'AMAP';
    const field = isTime ? 'time' : 'reps';

    // If the user is mutating an AMAP/ALAP token they're sitting in the middle
    // of, wipe the whole token (token-only in CSV input). Empty string replaces
    // the keyword; comma separators remain intact.
    const wipeResult = wipeRepKeywordOnMutation(
      e,
      value,
      selectionStart,
      selectionEnd,
      [keyword],
      { backspaceDeletes: true },
    );
    if (wipeResult.wiped) {
      e.preventDefault();
      setRepsInputValue(wipeResult.newValue);
      handleInputChange(field, wipeResult.newValue);
      requestAnimationFrame(() => {
        target.setSelectionRange(wipeResult.newCursor, wipeResult.newCursor);
      });
      return;
    }

    // Handle 'a' or 'A' key press for keyword auto-expansion
    if (key.toLowerCase() === 'a') {
      const beforeCursor = value.substring(0, selectionStart);
      const isAtValueStart = beforeCursor === '' || beforeCursor.endsWith(', ') || beforeCursor.endsWith(',');

      if (isAtValueStart) {
        e.preventDefault();
        const beforeSelection = value.substring(0, selectionStart);
        const afterSelection = value.substring(selectionEnd);
        const newValue = `${beforeSelection}${keyword}${afterSelection}`;
        const newCursorPosition = selectionStart + keyword.length;

        setRepsInputValue(newValue);
        handleInputChange(field, newValue);

        requestAnimationFrame(() => {
          target.setSelectionRange(newCursorPosition, newCursorPosition);
        });
      }
    }
  };

  const handlePercentPRBlur = () => {
    if (percentPR) {
      const prArray = percentPR
        .split(',')
        .map((pr) => pr.trim())
        .filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);

      // Check if all %PR values are valid and collect invalid ones
      const invalidValues = [];
      prArray.forEach((pr, index) => {
        const isValid = /^\d+$/.test(pr); // Only whole numbers allowed
        if (!isValid) {
          invalidValues.push({ value: pr, position: index + 1 });
        }
      });

      // If any %PR is invalid, show specific error
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setFieldError('percentPR', `Invalid %PR format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use whole numbers only.`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('percentPR', `Invalid %PR format for values: ${invalidList}. Use whole numbers only.`);
        }
        return;
      }

      // Check if the number of %PR values matches the set count
      if (prArray.length !== setCount && prArray.length !== 1) {
        setFieldError('percentPR', `Expected ${setCount} %PR values or 1 value (to apply to all sets), but have ${prArray.length} values.`);
      } else {
        setFieldError('percentPR', null);
      }
    } else {
      setFieldError('percentPR', null);
    }
  };

  const handleLoadInputChange = (value) => {
    setLoadInputValue(value);

    // For load, store the raw array and validate without auto-normalization
    const loadArray = value
      .split(',')
      .map((loadVal) => loadVal.trim())
      .filter(Boolean);

    // Validate format but don't auto-normalize
    const setCount = parseInt(setNumberValue, 10);
    const invalidValues = [];

    // Check each value for valid format (numbers, ranges, or plus notation)
    loadArray.forEach((loadVal, index) => {
      if (!isValidRangeToken(loadVal)) {
        invalidValues.push({ value: loadVal, position: index + 1 });
      }
    });

    // Show format errors
    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setFieldError('load', `Invalid load format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      } else {
        const invalidList = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setFieldError('load', `Invalid load format for values: ${invalidList}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      }
      return;
    }

    // Show count mismatch errors (but don't auto-normalize)
    if (loadArray.length !== setCount && loadArray.length !== 1 && loadArray.length > 0) {
      setFieldError('load', 'Enter a load value to apply to all sets.');
    } else {
      setFieldError('load', null);
    }
  };

  const handleLoadBlur = () => {
    if (loadInputValue && loadInputValue.trim() !== '') {
      const loadArray = loadInputValue
        .split(',')
        .map((loadVal) => loadVal.trim())
        .filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);

      // Check if all load values are valid and collect invalid ones
      const invalidValues = [];
      loadArray.forEach((loadVal, index) => {
        if (!isValidRangeToken(loadVal)) {
          invalidValues.push({ value: loadVal, position: index + 1 });
        }
      });

      // If any load is invalid, show specific error
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setFieldError('load', `Invalid load format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('load', `Invalid load format for values: ${invalidList}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        }
        return;
      }

      // Normalize single values to fill all sets on blur
      if (loadArray.length === 1) {
        const normalizedLoad = Array(setCount).fill(loadArray[0]);
        setLoadInputValue(normalizedLoad.join(', '));
        setFieldError('load', null);
      } else if (loadArray.length === setCount) {
        setFieldError('load', null);
      } else {
        setFieldError('load', `Expected ${setCount} load values or 1 value (to apply to all sets), but have ${loadArray.length} values.`);
      }
    } else {
      setFieldError('load', null);
    }
  };

  const typeFormatter = (typeDescription) => {
    if (typeDescription === 'lift') {
      return 'lift';
    }
    if (typeDescription === 'cardio') {
      return 'saq+c';
    }
    if (typeDescription === 'circuit') {
      return 'circuit';
    }
    return '';
  };

  const errorAddReset = () => {
    setIsAdding(false);
  };

  /**
 * Handles the saving of a calendar exercise by preparing and dispatching the required data.
 *
 * @param {number} contentIndexParam - Index of the content item being saved.
 */
  const handleSaveCalendarExercise = (contentIndexParam) => {
    // Check if reps field is empty when reps are enabled
    if (primaryRowSwitch.reps && (!exerciseData.reps || exerciseData.reps.length === 0 || (exerciseData.reps.length === 1 && exerciseData.reps[0] === ''))) {
      showErrorToast('Reps field cannot be empty');
      return;
    }

    // Defense-in-depth: re-validate and normalize reps right before submission so no
    // case-drifted ("Amap") or invalid ("AMRAP") tokens can leak past the errors gate.
    const submissionKeyword = isTime ? 'ALAP' : 'AMAP';
    const repsForValidation = Array.isArray(exerciseData.reps) ? exerciseData.reps : [];
    const { isValid: repsAreValid, values: submissionReps } = normalizeRepArray(
      repsForValidation,
      [submissionKeyword],
    );
    if (primaryRowSwitch.reps && !repsAreValid) {
      showErrorToast(`Invalid reps value. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "${submissionKeyword}".`);
      return;
    }

    // Proceed only if there are no errors.
    if (!Object.values(errors).some(Boolean)) {
    // Indicate that the saving process has started.
      setIsAdding(true);

      // Parse load values if present
      let loadArray = [];
      if (loadInputValue && loadInputValue.trim() !== '' && !isBodyweight) {
        loadArray = loadInputValue
          .split(',')
          .map((loadVal) => loadVal.trim())
          .filter(Boolean);
      }

      // Generate an array representing the details for each set.
      const setSubmissionArray = Array.from({ length: setNumberValue }, (_, i) => {
        const setData = {
          setNumber: i + 1,
          reps: submissionReps[i]
          ?? submissionReps[0],
          // Use specific reps for the set, defaulting to the first value if undefined.
        };

        // Handle load mapping to individual sets
        if (loadArray.length > 0) {
          setData.prescribedValue = toFieldValue(loadArray[i]
            ?? loadArray[0]); // Use specific load for the set, defaulting to first value
        }

        // Handle %PR mapping to individual sets
        if (percentPR) {
          const prArray = percentPR
            .split(',')
            .map((pr) => pr.trim())
            .filter(Boolean);
          if (prArray.length > 0) {
            setData.percent = prArray[i]
            ?? prArray[0]; // Use specific %PR for the set, defaulting to first value
          }
        }

        return setData;
      });

      // Create the main submission object with exercise details.
      const submissionObject = {
        coachCompletion, // Indicates if the coach marks this exercise as complete.
        completionOnly, // If true, only completion is tracked.
        completionRequired: true, // Ensure completion is required for this exercise.
        eachSide: isLeftAndRight, // Flag for tracking each side of the body separately.
        trackRepCount, // Whether to track the number of reps.
        trackVolumeLoad, // Whether to track volume load.
        forceMaxUpdate: forcePrUpdate,
        maxTrackingDisabled: disablePrUpdate,
        repIntensityUnit: null, // Reserved for rep intensity unit, currently null.
        type: content.type === 'T' ? 'tag' : typeFormatter(content.typeDescription), // Exercise type derived from a formatter.
        sets: setSubmissionArray, // Array of set details.
        showSessionBreakItems: false, // Whether session break items are shown (disabled here).
        repsOnly: isBodyweight, // Indicates if this is a bodyweight-only exercise.
        repType: isTime ? 'time' : 'reps', // Specifies whether the exercise tracks time or reps.
        groupingLetter: content.groupingLetter,
        groupingColorCode: content.groupingColorCode,
        /** Conditional key based on programType: "date" OR "dayNumber" */
        [programType === 'free-form-program' ? 'dayNumber' : 'date']:
        programType === 'free-form-program'
          ? dayNumber
          : moment(effectiveDate).format('YYYY-MM-DD'),
      };

      if (content.type === 'T') {
        submissionObject.tagId = content.exerciseId;
      } else {
        submissionObject.exerciseId = content.exerciseId;
      }

      // Optional properties based on exercise settings.
      if (tempo && primaryRowSwitch.tempo) {
        submissionObject.tempo = tempo; // Add tempo if applicable.
      }
      if (content.description) {
        submissionObject.description = content.description;
        // Include exercise description if provided.
      }
      if (peakPowerEnum && primaryRowSwitch['peak power']) {
        submissionObject.trackPeakPower = peakPowerEnum; // Add peak power tracking if enabled.
      }
      if (rest && primaryRowSwitch.rest) {
        submissionObject.restTime = rest; // Add rest time between sets if specified.
      }
      if (primaryRowSwitch.velocity) {
        submissionObject.trackBarSpeed = primaryRowSwitch.velocity; // Track bar speed if enabled.
      }
      if (localAdditionalInfo) {
        submissionObject.additionalInfo = localAdditionalInfo; // Include any additional local info.
      }

      // Format the date for dispatching.
      const formattedDate = moment(date).format('YYYY-MM-DD');

      // Dispatch the save action with the submission object.
      if (programType === 'free-form-program') {
        dispatch(saveNonDateBasedWorkoutExercise(
          currentUser, // Current user performing the save.
          contentIndexParam, // Index of the content item.
          submissionObject, // Submission details.
          dayNumber, // program 0 indexed dayNumber.
          errorAddReset, // Error handling/reset callback.
          {
            onSuccess: () => {
            // Trigger focus to next preSave item
              dispatch(focusNextPreSaveProgramItem(contentIndexParam, dayNumber));
            },
          },
          content.assignedId,
        ));
      } else {
        dispatch(saveCalendarExercise(
          currentUser, // Current user performing the save.
          contentIndexParam, // Index of the content item.
          submissionObject, // Submission details.
          formattedDate, // Formatted date.
          errorAddReset, // Error handling/reset callback.
          {
            onSuccess: () => {
            // Trigger focus to next preSave item
              dispatch(focusNextPreSaveItem(contentIndexParam, effectiveDate));
            },
          },
          content.assignedId,
        ));
      }
    }
  };

  /** Properties whose input is editable by the coach. Athlete-input-only
   * fields (peak power, velocity, bodyweight) are disabled, so we don't
   * auto-focus them when added. */
  const EDITABLE_FOCUS_PROPS = new Set(['reps', 'time', 'load', '%pr', 'rest', 'tempo']);

  /** Auto-focus the first input of a newly added property. Deferred so
   * the new row has rendered before we query for it. */
  const focusAddedProperty = useCallback((name) => {
    if (!EDITABLE_FOCUS_PROPS.has(name)) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-add-focus="${content.assignedId}-${name}"]`);
      if (el) {
        el.focus();
        if (typeof el.select === 'function') el.select();
      }
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.assignedId]);

  const rowValueDecider = (name, shouldShimmer) => {
    if (shouldShimmer) {
      return (
        <Shimmer>
          <RowValueContainer shimmer>
            <ShimmerRowValue />
          </RowValueContainer>
        </Shimmer>
      );
    }
    if (name === 'tempo') {
      return (
        <RowValueContainer>
          <RowValue
            data-add-focus={`${content.assignedId}-tempo`}
            value={tempo}
            onChange={(e) => setTempoFunction(e.target.value)}
            onBlur={saveStateToRedux}
          />
        </RowValueContainer>
      );
    }
    if (name === 'rest') {
      return (
        <RowValueContainer>
          <RowValue
            data-add-focus={`${content.assignedId}-rest`}
            type='number'
            value={rest}
            onChange={(e) => setRestFunction(e.target.value)}
            onBlur={saveStateToRedux}
          />
        </RowValueContainer>
      );
    }
    if (name === '%pr') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              data-add-focus={`${content.assignedId}-%pr`}
              isError={!!errors.percentPR}
              type='text'
              value={percentPR}
              onChange={(e) => setPercentPRFunction(e.target.value)}
              onBlur={() => {
                handlePercentPRBlur();
                saveStateToRedux();
              }}
            />
            {errors.percentPR && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.percentPR}
              >
                {errors.percentPR}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    if (name === 'load' || name === 'bodyweight') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              data-add-focus={name === 'load' ? `${content.assignedId}-load` : undefined}
              isError={name === 'load' && !!errors.load && !isBodyweight}
              type='text'
              value={isBodyweight ? '' : loadInputValue}
              onChange={(e) => handleLoadInputChange(e.target.value)}
              onBlur={() => {
                handleLoadBlur();
                saveStateToRedux();
              }}
              placeholder={name === 'bodyweight' ? '-' : null}
              textColor={name === 'bodyweight' ? colors.textPlaceholder : colors.text}
              disabled={isBodyweight}
            />
            {name === 'load' && errors.load && !isBodyweight && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.load}
              >
                {errors.load}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    if (name === 'peak power') {
      if (peakPowerEnum === 1) {
        return (
          <RowValueContainer>
            <RowValue
              disabled
              placeholder='-'
              textColor={colors.textPlaceholder}
            />
          </RowValueContainer>
        );
      }
      return (
        <RowValueContainer>
          <RowValue
            disabled
            placeholder='Left'
            textColor={colors.textPlaceholder}
            style={{
              marginRight: 8,
            }}
          />
          <RowValue
            disabled
            placeholder='Right'
            textColor={colors.textPlaceholder}
            style={{
              marginLeft: 8,
            }}
          />
        </RowValueContainer>
      );
    }
    if (name === 'velocity') {
      return (
        <RowValueContainer>
          <RowValue
            type='number'
            disabled
            placeholder='-'
            textColor={colors.textPlaceholder}
          />
        </RowValueContainer>
      );
    }
    return (
      <RowValueContainer>
        <InputWithErrorWrapper>
          <RowValue
            data-add-focus={(name === 'reps' || name === 'time') ? `${content.assignedId}-${name}` : undefined}
            isError={(name === 'reps' || name === 'time') && !!errors.reps}
            type='text'
            value={(name === 'reps' || name === 'time') ? repsInputValue : undefined}
            onChange={(e) => handleInputChange(name, e.target.value)}
            onBlur={(name === 'reps' || name === 'time') ? () => {
              handleRepsBlur();
              saveStateToRedux();
            } : saveStateToRedux}
            onKeyDown={(name === 'reps' || name === 'time') ? handleRepsKeyDown : null}
            data-focus-target={
              // Add focus target to the first editable
              // field that's visible (set field takes priority)
              (name === 'reps' && primaryRowSwitch.reps && !primaryRowSwitch.set)
              || (name === 'time' && primaryRowSwitch.time && !primaryRowSwitch.set && !primaryRowSwitch.reps)
              || (name === 'load' && primaryRowSwitch.load && !primaryRowSwitch.set && !primaryRowSwitch.reps && !primaryRowSwitch.time)
                ? content.assignedId
                : undefined
            }
          />
          {(name === 'reps' || name === 'time') && errors.reps && (
            <InputError
              data-tooltip-id={errorTooltipId}
              data-tooltip-content={errors.reps}
            >
              {errors.reps}
            </InputError>
          )}
        </InputWithErrorWrapper>
      </RowValueContainer>
    );
  };

  const updateColumnName = useCallback((oldName, newName) => {
    // Prevent updating the add-property column
    if (oldName === 'add-property' || newName === 'add-property') return;

    setPrimaryRows(
      (prevRows2) => prevRows2.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitch((prevSwitch) => ({
      ...prevSwitch,
      [oldName]: false,
      [newName]: true,
    }));
  }, []);

  const findCorrectUnit = (unitContent) => {
    if (unitContent?.unitAbbr) {
      if (unitContent?.unitAbbr === 'wgt') {
        // orgWeight: 1 = kg, 0 = lbs
        const weightUnit = currentUser?.userSettings?.orgWeight === 1 ? 'kg' : 'lbs';
        return `(${weightUnit})`;
      }
      return `(${unitContent?.unitAbbr})`;
    }
    return '';
  };

  const getTooltipId = (primaryRowName) => {
    if (primaryRowName === 'reps') return repsTooltipId;
    if (primaryRowName === 'time') return timeTooltipId;
    if (primaryRowName === 'peak power') return peakPowerTooltipId;
    if (primaryRowName === 'velocity') return velocityTooltipId;
    return undefined;
  };

  /** SUPERSET picker — A–Z plus a clear option. */
  const SUPERSET_OPTIONS = [
    { letter: null, color: null },
    ...Object.entries(SUPERSET_DEFAULTS).map(([letter, color]) => ({ letter, color })),
  ];

  const chooseSuperset = useCallback((letter, color) => {
    setIsSupersetOpen(false);
    /** `content` (Redux) is the single source of truth for grouping —
     * dispatching here is what this row's swatch reads on next render, and
     * it's what the older dropdown/kebab/bulk-action pickers read too, so
     * both stay in sync automatically. Already durable in Redux, so no
     * separate local-state backup is needed for these two fields. */
    const updatedItem = { ...content, groupingLetter: letter, groupingColorCode: color };
    if (programType === 'free-form-program') {
      dispatch(
        updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, contentIndex),
      );
    } else {
      dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, contentIndex));
    }
    /** Return focus to the trigger after dismissing the popover. */
    setTimeout(() => supersetButtonRef.current?.focus(), 0);
  }, [
    content, programType, dayNumber,
    selectedProgramDayNumber, contentIndex, endpointFormattedDate, dispatch,
  ]);

  /** Type-to-select: pressing a letter A–Z picks that grouping directly,
   * and Backspace / Delete / 0 clears it (back to None). Works whether
   * the trigger button is focused or the popover is open. Returns true
   * when the key was handled so callers can preventDefault. */
  const selectSupersetByKey = useCallback((e) => {
    /** Modifier combos are app or browser shortcuts — Cmd+I opens
     * Additional Info, Cmd+D deletes — never a grouping pick. Let them
     * through so the shortcut owner handles them and focus stays where
     * that owner put it. */
    if (e.metaKey || e.ctrlKey || e.altKey) return false;
    const { key } = e;
    if (key === 'Backspace' || key === 'Delete' || key === '0') {
      chooseSuperset(null, null);
      return true;
    }
    if (/^[a-zA-Z]$/.test(key)) {
      const letter = key.toUpperCase();
      const color = SUPERSET_DEFAULTS[letter];
      if (color) {
        chooseSuperset(letter, color);
        return true;
      }
    }
    return false;
  }, [chooseSuperset]);

  /** Outside-click + Escape to close the superset popover. */
  useEffect(() => {
    if (!isSupersetOpen) return undefined;
    const onMouseDown = (e) => {
      if (supersetPopoverRef.current?.contains(e.target)) return;
      if (supersetButtonRef.current?.contains(e.target)) return;
      setIsSupersetOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        /** This popover owns the Escape while it's open. Without stopping
         * it here the MonthEditPopover window listener also fires and
         * closes the whole exercise entry behind it. */
        e.stopPropagation();
        setIsSupersetOpen(false);
        supersetButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isSupersetOpen]);

  /** Anchor the portaled popover under the trigger. Re-position on
   * open, and on scroll/resize so it tracks if the workout list scrolls
   * underneath. Falls back to a coord clamp if the popover would
   * overflow the viewport. */
  const computeSupersetPos = useCallback(() => {
    const btn = supersetButtonRef.current;
    if (!btn) return null;
    const rect = btn.getBoundingClientRect();
    const POPOVER_WIDTH = 220;
    const VIEWPORT_PAD = 8;
    let { left } = rect;
    if (left + POPOVER_WIDTH > window.innerWidth - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, window.innerWidth - POPOVER_WIDTH - VIEWPORT_PAD);
    }
    return { top: rect.bottom + 4, left };
  }, []);

  useLayoutEffect(() => {
    if (!isSupersetOpen) return undefined;
    setSupersetPopoverPos(computeSupersetPos());
    const onScrollOrResize = () => {
      const next = computeSupersetPos();
      if (!next) return;
      setSupersetPopoverPos((prev) => {
        if (prev && prev.top === next.top && prev.left === next.left) return prev;
        return next;
      });
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isSupersetOpen, computeSupersetPos]);

  /** Own the arrow keys while the popover is open — they move the chip focus
   * here, and useCalendarKeyboardScroll would otherwise scroll the calendar
   * a row underneath at the same time. */
  useEffect(() => {
    if (!isSupersetOpen) return undefined;
    return claimArrowKeys();
  }, [isSupersetOpen]);

  /** Initialize focus on the currently-selected chip (or the clear
   * option if none) when the popover opens, so keyboard users land on
   * something obvious. */
  useEffect(() => {
    if (!isSupersetOpen) return;
    const idx = content.groupingLetter
      ? SUPERSET_OPTIONS.findIndex((o) => o.letter === content.groupingLetter)
      : 0;
    setSupersetFocusIndex(idx >= 0 ? idx : 0);
    setTimeout(() => supersetChipRefs.current[idx >= 0 ? idx : 0]?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupersetOpen]);

  const moveSupersetFocus = (delta) => {
    const next = (supersetFocusIndex + delta + SUPERSET_OPTIONS.length) % SUPERSET_OPTIONS.length;
    setSupersetFocusIndex(next);
    supersetChipRefs.current[next]?.focus();
  };

  return (
    <FormWrapper
      ref={tableContainerRef}
      onSubmit={(e) => {
        e.preventDefault();
        handleSaveCalendarExercise(contentIndex);
      }}
    >
      <SetsCountHeader>
        <SetsFieldGroup>
          <SetsCountLabel>Sets</SetsCountLabel>
          <SetsStepper>
            <StepperButton
              type='button'
              aria-label='Decrease sets'
              onClick={() => {
                const next = Math.max(1, parseInt(setNumberValue, 10) - 1);
                handleInputChange('sets', next);
                saveStateToRedux();
              }}
              disabled={parseInt(setNumberValue, 10) <= 1}
            >
              −
            </StepperButton>
            <StepperInput
              type='text'
              inputMode='numeric'
              value={setNumberValue}
              data-focus-target={primaryRowSwitch.set ? content.assignedId : undefined}
            /** Select-on-focus so a tabbed-in value is cleanly replaceable. */
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                /** Allow a transient empty value while editing; blur
               * normalizes it back to a valid count. */
                if (raw === '') {
                  handleInputChange('sets', '');
                  return;
                }
                let value = parseInt(raw, 10);
                /** Max is 10, so anything larger means the user typed a new
               * digit onto an existing value (e.g. 4 then 5 -> "45").
               * Treat that as replacing with the last digit typed -> 5.
               * "10" stays 10 because it isn't greater than 10. This gives
               * type-to-replace behavior without tracking caret/selection,
               * and typing 1 then 0 still reaches 10. */
                if (value > 10) {
                  value = parseInt(raw.slice(-1), 10);
                }
                value = Math.min(10, Math.max(1, value));
                handleInputChange('sets', value);
              }}
              onBlur={(e) => {
              /** Normalize empty / out-of-range back to a usable count. */
                const value = parseInt(e.target.value, 10);
                if (Number.isNaN(value) || value < 1) {
                  handleInputChange('sets', 1);
                }
                saveStateToRedux();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.target.blur();
                }
              }}
              aria-label='Number of sets'
            />
            <StepperButton
              type='button'
              aria-label='Increase sets'
              onClick={() => {
                const next = Math.min(10, parseInt(setNumberValue, 10) + 1);
                handleInputChange('sets', next);
                saveStateToRedux();
              }}
              disabled={parseInt(setNumberValue, 10) >= 10}
            >
              +
            </StepperButton>
          </SetsStepper>
          <SetsSubtext># of Sets: 1-10</SetsSubtext>
        </SetsFieldGroup>

        <SetsCountLabel>Superset</SetsCountLabel>
        <SupersetWrapper>
          <SupersetButton
            ref={supersetButtonRef}
            type='button'
            aria-haspopup='listbox'
            aria-expanded={isSupersetOpen}
            aria-label={content.groupingLetter ? `Superset ${content.groupingLetter}, change` : 'Select superset'}
            aria-keyshortcuts='A-Z'
            title='Type a letter (A–Z) to set a superset, or Backspace to clear'
            onClick={() => setIsSupersetOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsSupersetOpen(true);
                return;
              }
              /** Type a letter to pick a grouping without opening the
               * popover; Backspace/Delete/0 clears it. */
              if (selectSupersetByKey(e)) {
                e.preventDefault();
              }
            }}
          >
            {content.groupingLetter ? (
              <SupersetSwatch
                background={hexToRgba(
                  content.groupingColorCode || SUPERSET_DEFAULTS[content.groupingLetter],
                  0.2,
                )}
              >
                {content.groupingLetter}
              </SupersetSwatch>
            ) : (
              <SupersetEmptyHint>None</SupersetEmptyHint>
            )}
            <IcomoonReact iconSet={iconSet} size={9} icon='down-arrow' color={colors.textSecondary} />
          </SupersetButton>
          {isSupersetOpen && createPortal(
            <SupersetPopover
              ref={supersetPopoverRef}
              className='transposed-superset-popover'
              role='listbox'
              aria-label='Superset grouping'
              style={{
                top: supersetPopoverPos?.top ?? -9999,
                left: supersetPopoverPos?.left ?? -9999,
                visibility: supersetPopoverPos ? 'visible' : 'hidden',
              }}
              onKeyDown={(e) => {
                const step = SUPERSET_ARROW_STEPS[e.key];
                if (step !== undefined) {
                  e.preventDefault();
                  moveSupersetFocus(step);
                  return;
                }
                /** Type-ahead: a letter key picks that grouping directly. */
                if (selectSupersetByKey(e)) {
                  e.preventDefault();
                }
              }}
            >
              {SUPERSET_OPTIONS.map((opt, idx) => {
                const isSelected = content.groupingLetter === opt.letter;
                const bg = opt.color ? hexToRgba(opt.color, 0.2) : undefined;
                return (
                  <SupersetChip
                    key={opt.letter || 'none'}
                    type='button'
                    role='option'
                    aria-selected={isSelected}
                    isSelected={isSelected}
                    background={bg}
                    muted={!opt.letter}
                    tabIndex={supersetFocusIndex === idx ? 0 : -1}
                    ref={(el) => { supersetChipRefs.current[idx] = el; }}
                    onClick={() => chooseSuperset(opt.letter, opt.color)}
                    onFocus={() => setSupersetFocusIndex(idx)}
                  >
                    {opt.letter || '—'}
                  </SupersetChip>
                );
              })}
            </SupersetPopover>,
            document.body,
          )}
        </SupersetWrapper>

        <StatusBadgeScroller>
          {isLeftAndRight && <StateBadge>Each Side</StateBadge>}
          {coachCompletion && <StateBadge>Coach Completion</StateBadge>}
          {completionOnly && <StateBadge>Completion Only</StateBadge>}
          {forcePrUpdate && <StateBadge>Force PR</StateBadge>}
          {disablePrUpdate && <StateBadge>PR Off</StateBadge>}
          {trackVolumeLoad && <StateBadge>Track Volume</StateBadge>}
          {trackRepCount && <StateBadge>Track Reps</StateBadge>}
        </StatusBadgeScroller>
      </SetsCountHeader>

      <TransposedTable>
        {primaryRows.map((primaryRow) => {
          /** Sets is rendered as the stepper above; add-property is the
           * footer trigger below. Everything else gets a label-and-input
           * row that mirrors the saved-edit transposed layout. */
          if (primaryRow.name === 'set' || primaryRow.name === 'add-property') {
            return null;
          }
          if (!primaryRowSwitch[primaryRow.name]) return null;

          const tooltipId = getTooltipId(primaryRow.name);

          return (
            <TransposedRow key={`prop-${primaryRow.name}`}>
              <PropertyLabelCell>
                <NameAndDropdown>
                  <SwapPropDropdown
                    setPrimaryRowSwitch={setPrimaryRowSwitch}
                    primaryRowSwitch={primaryRowSwitch}
                    rowName={primaryRow.name}
                    size={12}
                    updateColumnName={updateColumnName}
                    name={primaryRow.name === 'load'
                      ? capitalizeWords('Load')
                      : primaryRow.label || capitalizeWords(primaryRow.name)}
                    unit={primaryRow.name === 'load' ? findCorrectUnit(content) : null}
                    peakPowerEnum={peakPowerEnum}
                    setPeakPowerEnum={setPeakPowerEnum}
                    setIsBodyweight={setIsBodyweight}
                    setIsTime={setIsTime}
                  />
                  {tooltipId && (
                    <IcomoonReact
                      iconSet={iconSet}
                      size={14}
                      icon='info2'
                      color={colors.textSecondary}
                      style={{ cursor: 'pointer', marginLeft: 4 }}
                      data-tooltip-id={tooltipId}
                      tabIndex={-1}
                      focusable='false'
                      aria-hidden='true'
                    />
                  )}
                </NameAndDropdown>
              </PropertyLabelCell>
              <PropertyInputCell>
                {rowValueDecider(primaryRow.name, isAdding)}
              </PropertyInputCell>
            </TransposedRow>
          );
        })}
      </TransposedTable>

      <TransposedFooter>
        <AddPropertySlot>
          <AddPropertyColumnComponent
            mode='input'
            primaryRows={primaryRows}
            primaryRowSwitch={primaryRowSwitch}
            setPrimaryRowSwitch={setPrimaryRowSwitch}
            setPrimaryRowSwitchState={setPrimaryRowSwitchState}
            assignedId={content.assignedId}
            closeOnSelect
            onPropertyAdded={focusAddedProperty}
            trigger={(
              <AddPropertyTrigger>
                <AddPropertyTriggerIcon>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={10}
                    icon='plus'
                    color='white'
                  />
                </AddPropertyTriggerIcon>
                Add Property
              </AddPropertyTrigger>
            )}
          />
        </AddPropertySlot>
        {!isAdding ? (
          <CompactSaveButton type='submit'>
            SAVE
            <IcomoonReact
              iconSet={iconSet}
              size={10}
              icon='down-arrow'
              color='white'
            />
          </CompactSaveButton>
        ) : (
          <Shimmer>
            <CompactSaveButton shimmer>
              SAVE
              <IcomoonReact
                iconSet={iconSet}
                size={10}
                icon='down-arrow'
                color='transparent'
              />
            </CompactSaveButton>
          </Shimmer>
        )}
      </TransposedFooter>
      {/* ReactTooltip components */}
      <ReactTooltip
        id={repsTooltipId}
        place='top'
        positionStrategy='fixed'
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
          maxWidth: '250px',
          padding: '10px 16px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Text fontSize='13px' fontWeight={400} color='white'>Accepted Inputs</Text>
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li>
              <Text fontSize='13px' fontWeight={400} color='white'>Absolute (e.g. 5)</Text>
            </li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Comma Separated (e.g. 12,10,8)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Range (e.g. 8-12)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Minimum (e.g. 8+)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>&quot;AMAP&quot; for as many as possible</Text></li>
          </ul>
        </div>
      </ReactTooltip>

      <ReactTooltip
        id={timeTooltipId}
        place='top'
        positionStrategy='fixed'
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
          maxWidth: '250px',
          padding: '10px 16px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Text fontSize='13px' fontWeight={400} color='white'>Accepted Inputs</Text>
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li>
              <Text fontSize='13px' fontWeight={400} color='white'>Absolute (e.g. 30)</Text>
            </li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Comma Separated (e.g. 30,20,10)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Range (e.g. 30-60)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Minimum (e.g. 30+)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>&quot;ALAP&quot; for as long as possible</Text></li>
          </ul>
        </div>
      </ReactTooltip>

      <ReactTooltip
        id={peakPowerTooltipId}
        place='top'
        positionStrategy='fixed'
        content='Athlete input only'
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
        }}
      />

      <ReactTooltip
        id={velocityTooltipId}
        place='top'
        positionStrategy='fixed'
        content='Athlete input only'
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
        }}
      />
      <ReactTooltip
        id={errorTooltipId}
        place='top'
        positionStrategy='fixed'
        delayShow={300}
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '12px',
          maxWidth: '260px',
          padding: '6px 10px',
          lineHeight: 1.3,
        }}
      />
    </FormWrapper>
  );
};

PreSaveTableTransposed.propTypes = {
  coachCompletion: PropTypes.bool.isRequired,
  trackVolumeLoad: PropTypes.bool.isRequired,
  forcePrUpdate: PropTypes.bool.isRequired,
  disablePrUpdate: PropTypes.bool.isRequired,
  isLeftAndRight: PropTypes.bool.isRequired,
  completionOnly: PropTypes.bool.isRequired,
  trackRepCount: PropTypes.bool.isRequired,
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  localAdditionalInfo: PropTypes.string.isRequired,
  measure: PropTypes.func.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
  setCoachCompletion: PropTypes.func.isRequired,
  setCompletionOnly: PropTypes.func.isRequired,
  setIsLeftAndRight: PropTypes.func.isRequired,
  setTrackVolumeLoad: PropTypes.func.isRequired,
  setTrackRepCount: PropTypes.func.isRequired,
  setForcePrUpdate: PropTypes.func.isRequired,
  setDisablePrUpdate: PropTypes.func.isRequired,
};

PreSaveTableTransposed.defaultProps = {
  date: null,
  dayNumber: null,
};

export default PreSaveTableTransposed;
